import { useEffect, useState, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import api from "@/lib/api";
import { toast } from "sonner";
import { Landmark, RefreshCcw, Unlink, Plus } from "lucide-react";

export default function BankSync({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/plaid/status");
    setStatus(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plaid/link_token");
      setLinkToken(data.link_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start bank connection");
    }
    setBusy(false);
  };

  const onSuccess = useCallback(async (public_token) => {
    setBusy(true);
    try {
      const { data } = await api.post("/plaid/exchange", { public_token });
      if (data.duplicate) {
        toast.info("This bank is already connected.");
      } else {
        toast.success(`Bank connected — imported ${data.imported} transactions`);
      }
      await load();
      onSynced && onSynced();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect bank");
    } finally {
      setBusy(false);
      setLinkToken(null);
    }
  }, [load, onSynced]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => { setLinkToken(null); },
  });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const syncOne = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plaid/sync?item_id=${itemId}`);
      toast.success(`Refreshed: ${data.imported} new · ${data.modified} updated`);
      await load();
      onSynced && onSynced();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
    setBusy(false);
  };

  const syncAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plaid/sync");
      toast.success(`Synced ${status.items.length} banks: ${data.imported} new · ${data.modified} updated`);
      await load();
      onSynced && onSynced();
    } catch (err) {
      toast.error("Sync failed");
    }
    setBusy(false);
  };

  const disconnect = async (itemId, name) => {
    if (!confirm(`Disconnect ${name}? Your existing transactions stay.`)) return;
    setBusy(true);
    try {
      await api.delete(`/plaid/disconnect/${itemId}`);
      toast.info("Bank disconnected");
      await load();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="plaid-not-configured">
        <div className="flex items-center gap-2 font-semibold text-slate-800"><Landmark size={16} /> Bank auto-sync</div>
        <p className="mt-1 text-xs">Add your Plaid <code>CLIENT_ID</code> and <code>SECRET</code> to enable automatic bank connection. Meanwhile, upload a CSV.</p>
      </div>
    );
  }

  const items = status.items || [];

  if (items.length === 0) {
    const isProd = status.is_production;
    return (
      <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-5" data-testid="plaid-connect">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-300 text-[11px] uppercase tracking-widest font-semibold"><Landmark size={12} /> Auto sync</div>
          <span
            data-testid="plaid-env-badge"
            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${isProd ? "bg-emerald-500 text-slate-900" : "bg-amber-400/20 text-amber-300 border border-amber-400/40"}`}
          >
            {isProd ? "Live" : "Sandbox"}
          </span>
        </div>
        <div className="mt-2 font-manrope text-lg font-bold">Connect your bank</div>
        <p className="mt-1 text-sm text-slate-300">
          {isProd
            ? "Import 90 days of transactions instantly."
            : <>Import 90 days of transactions instantly. Sandbox login: <code>user_good / pass_good</code></>}
        </p>
        <button
          data-testid="plaid-connect-btn"
          onClick={startLink}
          disabled={busy}
          className="mt-4 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-3 font-semibold disabled:opacity-60 transition-colors duration-200"
        >
          {busy ? "Opening…" : "Connect bank"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="plaid-connected">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          <Landmark size={12} /> Connected banks · {items.length}
          <span
            data-testid="plaid-env-badge"
            className={`ml-1 text-[9px] rounded-full px-1.5 py-0.5 font-bold ${status.is_production ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
          >
            {status.is_production ? "LIVE" : "SANDBOX"}
          </span>
        </div>
        <button
          data-testid="plaid-sync-all-btn"
          onClick={syncAll}
          disabled={busy}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1"
        >
          <RefreshCcw size={12} className={busy ? "animate-spin" : ""} /> Sync all
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.item_id} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between" data-testid={`plaid-item-${it.item_id}`}>
            <div className="min-w-0">
              <div className="font-manrope font-bold text-slate-900 truncate">{it.institution_name || "Bank"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {(it.accounts || []).length} account{(it.accounts || []).length === 1 ? "" : "s"}
                {it.last_synced_at && ` · synced ${new Date(it.last_synced_at).toLocaleDateString()}`}
              </div>
            </div>
            <div className="flex gap-1.5 ml-3 shrink-0">
              <button data-testid={`plaid-sync-${it.item_id}`} onClick={() => syncOne(it.item_id)} disabled={busy} className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center transition-colors" aria-label="Refresh"><RefreshCcw size={14} strokeWidth={1.75} className={busy ? "animate-spin" : ""} /></button>
              <button data-testid={`plaid-disconnect-${it.item_id}`} onClick={() => disconnect(it.item_id, it.institution_name)} disabled={busy} className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors" aria-label="Disconnect"><Unlink size={14} strokeWidth={1.75} /></button>
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="plaid-connect-another-btn"
        onClick={startLink}
        disabled={busy}
        className="mt-3 w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 py-3 font-semibold text-slate-700 text-sm flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
      >
        <Plus size={16} strokeWidth={2} /> Connect another bank
      </button>
    </div>
  );
}
